import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAtom } from 'jotai';
import { toast } from 'react-toastify';
import Modal from './Modal';
import { autoPickSubstitutions } from '../helpers/cardHelpers';
import { userItemsAtom } from '../atoms/itemAtoms';
import { useItem, ITEM_TYPES } from '../api/items';

/**
 * Shown when POST /games returns 409 PLAYERS_FATIGUED.
 *
 * Pre-loads the best available substitute per position (by rating).
 * User can override any pick before confirming.
 * The same substitute card cannot be assigned to two slots.
 *
 * Energy drink button per row lets the user heal a player instead of subbing.
 * Modal manages its own local fatigued list so healed players are removed inline.
 */
const FatiguedSubstitutionModal = ({ unavailableCards, availableSubstitutes, onConfirm, onCancel, title, onAllHealed }) => {
    // Local copies so we can remove healed players without parent duplicating logic
    const [localUnavailable, setLocalUnavailable] = useState(unavailableCards);
    const [localSubstitutes, setLocalSubstitutes] = useState(availableSubstitutes);
    const [pickedSubs, setPickedSubs] = useState(() => autoPickSubstitutions(availableSubstitutes));
    const [healingCard, setHealingCard] = useState(null);

    const [userItems, setUserItems] = useAtom(userItemsAtom);
    const energyDrinkCount = userItems[ITEM_TYPES.ENERGY_DRINK] || 0;

    // For row X: options are substitutes excluding picks made in OTHER rows
    const getOptionsForGroup = group => {
        const otherPicks = new Set(
            Object.entries(pickedSubs)
                .filter(([key]) => key !== group.forInventoryID)
                .map(([, val]) => val)
        );
        return [...group.substitutes]
            .sort((a, b) => b.rating - a.rating)
            .filter(sub => !otherPicks.has(sub.inventoryID));
    };

    const handlePickChange = (forInventoryID, toInventoryID) => {
        setPickedSubs(prev => ({ ...prev, [forInventoryID]: toInventoryID }));
    };

    const handleUseDrink = useCallback(async (inventoryID) => {
        if (energyDrinkCount <= 0 || healingCard) return;
        setHealingCard(inventoryID);

        // Optimistic update — revert restores from atom's own prev, not a captured value
        setUserItems(prev => ({ ...prev, [ITEM_TYPES.ENERGY_DRINK]: Math.max(0, (prev[ITEM_TYPES.ENERGY_DRINK] || 0) - 1) }));

        try {
            const result = await useItem(ITEM_TYPES.ENERGY_DRINK, inventoryID);
            if (result.status === 'used' || result.status === 'already_available') {
                if (result.status === 'used') {
                    toast.success('Player stamina restored!');
                } else {
                    toast.info('Player is already rested!');
                }
                // Use functional updates to avoid stale closure on rapid clicks
                setLocalUnavailable(prev => {
                    const updated = prev.filter(c => c.inventoryID !== inventoryID);
                    if (updated.length === 0 && onAllHealed) {
                        onAllHealed();
                    }
                    return updated;
                });
                setLocalSubstitutes(prev => prev.filter(g => g.forInventoryID !== inventoryID));
            } else {
                // Revert: re-increment from atom's prev state
                setUserItems(prev => ({ ...prev, [ITEM_TYPES.ENERGY_DRINK]: (prev[ITEM_TYPES.ENERGY_DRINK] || 0) + 1 }));
                toast.error(result.status === 'insufficient' ? 'No energy drinks remaining.' : (result.reason || 'Could not use item.'));
            }
        } catch {
            setUserItems(prev => ({ ...prev, [ITEM_TYPES.ENERGY_DRINK]: (prev[ITEM_TYPES.ENERGY_DRINK] || 0) + 1 }));
            toast.error('Failed to use energy drink.');
        } finally {
            setHealingCard(null);
        }
    }, [energyDrinkCount, healingCard, onAllHealed, setUserItems]);

    const hasNoSubAvailable = localSubstitutes.some(group => group.substitutes.length === 0);
    const allPicksMade = localSubstitutes.every(
        group => group.substitutes.length === 0 || !!pickedSubs[group.forInventoryID]
    );
    const canStart = !hasNoSubAvailable && allPicksMade && localSubstitutes.length > 0;

    const handleConfirm = () => {
        const substitutions = Object.entries(pickedSubs)
            // Only include subs for players still in the local list (not healed)
            .filter(([fromID]) => localSubstitutes.some(g => g.forInventoryID === fromID))
            .map(([fromInventoryID, toInventoryID]) => ({
                fromInventoryID,
                toInventoryID,
            }));
        onConfirm(substitutions);
    };

    return (
        <Modal onClose={onCancel} title={title}>
            <div className="fatigue-sub-modal">
                <h3 className="fatigue-sub-title">Some Players Need Rest</h3>
                <p className="fatigue-sub-subtitle">
                    Pick replacements for your tired players. The best available options are pre-selected.
                </p>

                <div className="fatigue-drink-count">
                    <span className="energy-drink-icon">⚡</span> {energyDrinkCount}
                </div>

                <div className="fatigue-sub-list">
                    {localSubstitutes.map(group => {
                        const tiredPlayer = localUnavailable.find(c => c.inventoryID === group.forInventoryID);
                        const options = getOptionsForGroup(group);
                        const currentPick = pickedSubs[group.forInventoryID] || '';

                        return (
                            <div key={group.forInventoryID} className="fatigue-sub-row">
                                <div className="fatigue-sub-top">
                                    <span className="fatigue-position-badge">{group.forPosition}</span>
                                    <div className="fatigue-player-info">
                                        <span className="fatigue-player-name">{tiredPlayer?.name ?? '—'}{tiredPlayer?.rating ? ` (${tiredPlayer.rating})` : ''}</span>
                                        <span className="fatigue-reason">{tiredPlayer?.reason ?? 'Fatigued'}</span>
                                    </div>
                                    {tiredPlayer ? (
                                        <button
                                            className={`fatigue-drink-btn${energyDrinkCount > 0 ? '' : ' disabled'}`}
                                            onClick={() => handleUseDrink(group.forInventoryID)}
                                            disabled={energyDrinkCount <= 0 || healingCard === group.forInventoryID}
                                            title={energyDrinkCount > 0 ? 'Use Energy Drink' : 'No energy drinks available'}
                                        >
                                            <span className="energy-drink-icon">⚡</span>
                                        </button>
                                    ) : (
                                        <span className="fatigue-drink-btn disabled" />
                                    )}
                                </div>

                                <div className="fatigue-sub-pick">
                                    {options.length === 0 ? (
                                        <span className="fatigue-no-sub">No substitutes available</span>
                                    ) : (
                                        <select
                                            value={currentPick}
                                            onChange={e => handlePickChange(group.forInventoryID, e.target.value)}
                                            aria-label={`Substitute for ${tiredPlayer?.name ?? group.forPosition}`}
                                        >
                                            <option value="">Select substitute</option>
                                            {options.map(sub => (
                                                <option key={sub.inventoryID} value={sub.inventoryID}>
                                                    {sub.name} — {sub.rating} — {sub.positions}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {hasNoSubAvailable && (
                    <p className="fatigue-warning">
                        One or more positions have no available substitutes. You cannot start this game until
                        you acquire eligible players or wait for your roster to recover.
                    </p>
                )}

                <div className="fatigue-sub-actions">
                    <button className="boring_button" onClick={onCancel}>
                        Cancel
                    </button>
                    <button onClick={handleConfirm} disabled={!canStart}>
                        Start Game with Substitutes
                    </button>
                </div>
            </div>
        </Modal>
    );
};

FatiguedSubstitutionModal.propTypes = {
    unavailableCards: PropTypes.arrayOf(
        PropTypes.shape({
            inventoryID: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            teamPosition: PropTypes.string.isRequired,
            reason: PropTypes.string,
        })
    ).isRequired,
    availableSubstitutes: PropTypes.arrayOf(
        PropTypes.shape({
            forInventoryID: PropTypes.string.isRequired,
            forPosition: PropTypes.string.isRequired,
            substitutes: PropTypes.arrayOf(
                PropTypes.shape({
                    inventoryID: PropTypes.string.isRequired,
                    name: PropTypes.string.isRequired,
                    rating: PropTypes.number.isRequired,
                    positions: PropTypes.string,
                })
            ).isRequired,
        })
    ).isRequired,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    title: PropTypes.string,
    onAllHealed: PropTypes.func,
};

export default FatiguedSubstitutionModal;
